import { Flex, Progress, Text, Tooltip } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';
import { OktonDataGrid, OktonDataGridColumn } from '@okton/platform-ui';
import React, { useMemo } from 'react';
import { ContaDashboard } from '../../../services/hooks/useDashboard';
import formatValue from '../../../utils/formatValue';
import {
  formatDataHora,
  formatPercentual,
  formatUltimaAtualizacao,
} from '../formatacao';
import Painel from './Painel';

interface Props {
  contas: ContaDashboard[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  onAbrir: (conta: string) => void;
}

function corProgresso(percentual: number) {
  if (percentual >= 80) return 'green';
  if (percentual >= 50) return 'orange';
  return 'red';
}

const ContasTable: React.FC<Props> = ({
  contas,
  isLoading,
  isError,
  error,
  onRetry,
  onAbrir,
}) => {
  const linhas = useMemo(
    () =>
      contas.map(c => ({
        id: c.id_cts,
        conta: (
          <>
            <Text fontWeight="medium">{c.descricao_cts}</Text>
            <Text fontSize="xs" color="gray.400">
              {[c.nome_ban, c.numero_cts && `C/C ${c.numero_cts}`]
                .filter(Boolean)
                .join(' • ')}
            </Text>
          </>
        ),
        saldo: (
          <Text
            fontWeight="semibold"
            whiteSpace="nowrap"
            color={Number(c.saldo) < 0 ? 'red.500' : undefined}
          >
            {formatValue(c.saldo)}
          </Text>
        ),
        pendentes: (
          <>
            <Text
              fontWeight="semibold"
              color={c.pendentes > 0 ? 'orange.500' : 'gray.400'}
            >
              {c.pendentes.toLocaleString('pt-BR')}
            </Text>
            {c.divergencias > 0 && (
              <Text fontSize="xs" color="red.500" whiteSpace="nowrap">
                {c.divergencias} com divergência
              </Text>
            )}
          </>
        ),
        conciliado:
          c.percentual === null ? (
            <Text fontSize="xs" color="gray.400">
              Sem extrato no período
            </Text>
          ) : (
            <Flex align="center" gridGap="3" minW="180px">
              <Progress
                value={c.percentual}
                colorScheme={corProgresso(c.percentual)}
                size="sm"
                rounded="full"
                flex="1"
                aria-label={`${formatPercentual(c.percentual)} conciliado`}
              />
              <Text fontWeight="semibold" w="48px" textAlign="right">
                {formatPercentual(c.percentual)}
              </Text>
            </Flex>
          ),
        atualizacao: (
          <Tooltip
            label={
              c.ultimaatualizacao
                ? `Última importação de extrato: ${formatDataHora(
                    c.ultimaatualizacao,
                  )}`
                : 'Nenhum extrato importado nesta conta'
            }
            openDelay={300}
          >
            <Flex
              align="center"
              gridGap="1.5"
              whiteSpace="nowrap"
              color={c.sematualizacao ? 'orange.500' : undefined}
            >
              {c.sematualizacao && <AlertTriangle size={14} />}
              {formatUltimaAtualizacao(c.ultimaatualizacao)}
            </Flex>
          </Tooltip>
        ),
      })),
    [contas],
  );

  const colunas = useMemo<OktonDataGridColumn<typeof linhas[number]>[]>(
    () => [
      { key: 'conta', header: 'CONTA', render: linha => linha.conta },
      { key: 'saldo', header: 'SALDO', render: linha => linha.saldo },
      {
        key: 'pendentes',
        header: 'PENDENTES',
        render: linha => linha.pendentes,
      },
      {
        key: 'conciliado',
        header: 'CONCILIADO',
        render: linha => linha.conciliado,
      },
      {
        key: 'atualizacao',
        header: 'ÚLTIMA ATUALIZAÇÃO',
        render: linha => linha.atualizacao,
      },
    ],
    [],
  );

  return (
    <Painel
      titulo="Contas Bancárias"
      isLoading={isLoading}
      isError={isError}
      error={error}
      onRetry={onRetry}
      h="260px"
    >
      <OktonDataGrid
        rows={linhas}
        columns={colunas}
        getRowId={linha => linha.id}
        onRowClick={linha => onAbrir(linha.id)}
        hidePagination
        emptyMessage="Sem dados"
      />
    </Painel>
  );
};

export default ContasTable;
